/**
 * @fileoverview Modulo wrapper per l'integrazione con Swym Wishlist API
 *
 * Questo modulo gestisce tutte le operazioni relative alla wishlist dell'utente,
 * interfacciandosi con il servizio esterno Swym (https://swym.it/).
 *
 * Funzionalità principali:
 * - Inizializzazione del consenso cookie (GDPR compliant)
 * - Caricamento/creazione wishlist utente
 * - Aggiunta/rimozione prodotti dalla wishlist
 * - Verifica stato prodotto in wishlist
 * - Sistema di queue per gestire il caricamento asincrono di Swym
 *
 * @module wishlist
 * @requires window._swat - Swym SDK (caricato esternamente)
 * @requires window.Shopify - Shopify SDK
 *
 * @example
 * // Import delle funzioni
 * import { addToWishlist, removeFromWishlist, isInWishlist } from './wishlist';
 *
 * // Aggiungere un prodotto
 * addToWishlist(
 *   { epi: '123456', empi: '789', du: 'https://shop.com/product' },
 *   (data) => console.log('Aggiunto!', data),
 *   (err) => console.error('Errore', err)
 * );
 *
 * @fires theme:wishlist:loaded - Quando la wishlist è stata caricata/creata
 * @fires theme:wishlist:added - Quando un prodotto viene aggiunto
 * @fires theme:wishlist:removed - Quando un prodotto viene rimosso
 */
/**
 * Carica e configura il consenso dei cookie tramite Shopify Customer Privacy API.
 *
 * ⚠️ ATTENZIONE - FORZATURA NECESSARIA:
 * Questa funzione forza il consenso a `true` per analytics, marketing e preferences
 * perché il plugin Swym richiede OBBLIGATORIAMENTE il consenso tramite l'API nativa
 * di Shopify (`window.Shopify.customerPrivacy`) e NON supporta Cookiebot, che è il
 * sistema di gestione consensi utilizzato nel tema.
 *
 * Swym verifica internamente `window.Shopify.customerPrivacy.currentVisitorConsent()`
 * e se non trova il consenso impostato, blocca tutte le operazioni wishlist.
 * Cookiebot gestisce il consenso separatamente e non popola questa API Shopify.
 *
 * Questo è un workaround. Il consenso reale dell'utente è gestito da
 * Cookiebot a livello di tema. Qui "sblocchiamo" Swym per farlo funzionare.
 *
 * @function loadCookie
 * @returns {void}
 */
/** Contatore retry per loadCookie - evita loop infiniti */
let loadCookieRetries = 0;
const MAX_COOKIE_RETRIES = 50; // 50 * 100ms = 5 secondi max
export function loadCookie() {
    var _a, _b, _c;
    // Verifica che Shopify.loadFeatures sia disponibile (potrebbe non esserlo durante l'import iniziale)
    if (typeof ((_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.loadFeatures) !== 'function') {
        loadCookieRetries++;
        if (loadCookieRetries >= MAX_COOKIE_RETRIES) {
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.warn) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] Shopify.loadFeatures non disponibile dopo 5s, cookie consent non impostato');
            return;
        }
        // Riprova dopo un breve delay quando Shopify sarà completamente caricato
        setTimeout(loadCookie, 100);
        return;
    }
    window.Shopify.loadFeatures([
        {
            name: 'consent-tracking-api',
            version: '0.1',
        },
    ], error => {
        var _a, _b, _c;
        if (error) {
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Errore nel caricamento delle funzionalità di Shopify:', error);
            return;
        }
        // Forzatura consenso per Swym (vedi commento JSDoc sopra)
        (_c = window.Shopify.customerPrivacy) === null || _c === void 0 ? void 0 : _c.setTrackingConsent({
            "analytics": true,
            "marketing": true,
            "preferences": true
        }, () => { var _a, _b; return (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.success) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Consent captured'); });
    });
}
/**
 * Inizializza il caricamento della wishlist.
 *
 * Gestisce il caso in cui Swym SDK non sia ancora disponibile,
 * accodando la richiesta tramite `window.SwymCallbacks`.
 * Quando Swym sarà pronto, eseguirà automaticamente le callback in coda.
 *
 * @function loadWishlist
 * @returns {void}
 */
export function loadWishlist() {
    var _a, _b, _c, _d;
    if (typeof window._swat === 'undefined') {
        if (!window.SwymCallbacks) {
            window.SwymCallbacks = [];
        }
        window.SwymCallbacks.push(checkWishlist);
        (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] Swym non ancora caricato, checkWishlist messo in coda (queue length:', window.SwymCallbacks.length, ')');
    }
    else {
        (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.info) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist] Swym già disponibile, esecuzione immediata');
        checkWishlist();
    }
}
/**
 * Verifica l'esistenza della wishlist utente e la carica.
 *
 * Se non esiste nessuna wishlist, ne crea una nuova automaticamente.
 * La wishlist caricata viene salvata in `window.themeWishlist` per
 * accesso globale e viene dispatchato l'evento `theme:wishlist:loaded`.
 *
 * @function checkWishlist
 * @returns {void}
 * @fires theme:wishlist:loaded - Quando la wishlist è pronta
 */
export function checkWishlist() {
    var _a, _b, _c, _d, _e, _f, _g;
    try {
        // Verifica che Swym SDK sia disponibile e funzionante
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.fetchLists) !== 'function') {
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.warn) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] Swym SDK non disponibile, wishlist disabilitata');
            return;
        }
        (_e = (_d = window.LOG) === null || _d === void 0 ? void 0 : _d.debug) === null || _e === void 0 ? void 0 : _e.call(_d, '[Wishlist] → fetchLists chiamato');
        window._swat.fetchLists({
            callbackFn: (lists) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
                (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← fetchLists risposta raw:', JSON.stringify(lists));
                (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.debug) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist]   liste trovate:', (_e = lists === null || lists === void 0 ? void 0 : lists.length) !== null && _e !== void 0 ? _e : 0);
                // Se non ci sono liste, creane una nuova
                if (!lists || lists.length === 0) {
                    (_g = (_f = window.LOG) === null || _f === void 0 ? void 0 : _f.info) === null || _g === void 0 ? void 0 : _g.call(_f, '[Wishlist]   nessuna lista trovata → createList');
                    createList();
                    return;
                }
                (_j = (_h = window.LOG) === null || _h === void 0 ? void 0 : _h.debug) === null || _j === void 0 ? void 0 : _j.call(_h, '[Wishlist]   lista selezionata [0]: lid=', lists[0].lid, 'lname=', lists[0].lname, 'items=', (_l = (_k = lists[0].listcontents) === null || _k === void 0 ? void 0 : _k.length) !== null && _l !== void 0 ? _l : 0);
                // Salva la prima lista come wishlist globale
                window.themeWishlist = lists[0];
                (_o = (_m = window.LOG) === null || _m === void 0 ? void 0 : _m.success) === null || _o === void 0 ? void 0 : _o.call(_m, '[Wishlist] Wishlist caricata:', window.themeWishlist);
                // Notifica che la wishlist è pronta
                window.dispatchEvent(new CustomEvent('theme:wishlist:loaded'));
            },
            errorFn: (err) => { var _a, _b; return (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← fetchLists errore:', err); }
        });
    }
    catch (error) {
        (_g = (_f = window.LOG) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.call(_f, '[Wishlist] Errore critico in checkWishlist:', error);
    }
}
/**
 * Crea una nuova wishlist per l'utente.
 *
 * Viene chiamata automaticamente da `checkWishlist()` se non esistono liste.
 * Dopo la creazione usa direttamente la risposta dell'API per popolare `window.themeWishlist`,
 * evitando un secondo `fetchLists` che fallirebbe per i guest prima che il `di` (device identifier)
 * sia memorizzato da Swym.
 *
 * @function createList
 * @returns {void}
 */
export function createList() {
    var _a, _b, _c, _d, _e, _f, _g;
    try {
        // Verifica che Swym SDK sia disponibile
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.createList) !== 'function') {
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.warn) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] Swym SDK non disponibile per createList');
            return;
        }
        (_e = (_d = window.LOG) === null || _d === void 0 ? void 0 : _d.debug) === null || _e === void 0 ? void 0 : _e.call(_d, '[Wishlist] → createList chiamato (lname: "Wishlist")');
        window._swat.createList({
            lname: 'Wishlist'
        }, (newList) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← createList risposta raw:', JSON.stringify(newList));
            (_d = (_c = window.LOG) === null || _c === void 0 ? void 0 : _c.debug) === null || _d === void 0 ? void 0 : _d.call(_c, '[Wishlist]   lid:', newList === null || newList === void 0 ? void 0 : newList.lid);
            (_f = (_e = window.LOG) === null || _e === void 0 ? void 0 : _e.debug) === null || _f === void 0 ? void 0 : _f.call(_e, '[Wishlist]   di (device identifier):', newList === null || newList === void 0 ? void 0 : newList.di);
            (_h = (_g = window.LOG) === null || _g === void 0 ? void 0 : _g.debug) === null || _h === void 0 ? void 0 : _h.call(_g, '[Wishlist]   userinfo (loggato?):', newList === null || newList === void 0 ? void 0 : newList.userinfo);
            // Usa i dati restituiti direttamente invece di ri-chiamare fetchLists.
            // Per i guest, fetchLists richiederebbe il di (device identifier) già memorizzato
            // da Swym — ri-fetchare subito può causare lista vuota se il di non è ancora pronto.
            window.themeWishlist = newList;
            (_k = (_j = window.LOG) === null || _j === void 0 ? void 0 : _j.success) === null || _k === void 0 ? void 0 : _k.call(_j, '[Wishlist] Wishlist creata e salvata in themeWishlist');
            window.dispatchEvent(new CustomEvent('theme:wishlist:loaded'));
        }, (err) => { var _a, _b; return (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← createList errore:', err); });
    }
    catch (error) {
        (_g = (_f = window.LOG) === null || _f === void 0 ? void 0 : _f.error) === null || _g === void 0 ? void 0 : _g.call(_f, '[Wishlist] Errore critico in createList:', error);
    }
}
/**
 * Aggiunge un prodotto alla wishlist.
 *
 * @function addToWishlist
 * @param {SwymItem} product - Prodotto da aggiungere
 * @param {Function} onSuccess - Callback chiamata in caso di successo
 * @param {Function} onError - Callback chiamata in caso di errore
 * @returns {void}
 * @fires theme:wishlist:added - Con il dettaglio del prodotto aggiunto
 *
 * @example
 * addToWishlist(
 *   { epi: '47511834099867', empi: '8757020950683', du: 'https://shop.com/products/my-product' },
 *   (data) => console.log('Prodotto aggiunto:', data),
 *   (err) => console.error('Errore:', err)
 * );
 */
export function addToWishlist(product, onSuccess, onError) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    try {
        // Verifica che Swym SDK e wishlist siano disponibili
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.addToList) !== 'function') {
            const error = new Error('Swym SDK non disponibile');
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] addToWishlist fallito:', error);
            onError(error);
            return;
        }
        if (!((_d = window.themeWishlist) === null || _d === void 0 ? void 0 : _d.lid)) {
            const error = new Error('Wishlist non caricata');
            (_f = (_e = window.LOG) === null || _e === void 0 ? void 0 : _e.error) === null || _f === void 0 ? void 0 : _f.call(_e, '[Wishlist] addToWishlist fallito:', error);
            onError(error);
            return;
        }
        (_h = (_g = window.LOG) === null || _g === void 0 ? void 0 : _g.debug) === null || _h === void 0 ? void 0 : _h.call(_g, '[Wishlist] → addToList chiamato: lid=', window.themeWishlist.lid, 'product=', JSON.stringify(product));
        window._swat.addToList(window.themeWishlist.lid, product, (data) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← addToList risposta raw:', JSON.stringify(data));
            window.dispatchEvent(new CustomEvent('theme:wishlist:added', { detail: data }));
            onSuccess(data);
        }, (err) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← addToList errore:', err);
            onError(err);
        });
    }
    catch (error) {
        (_k = (_j = window.LOG) === null || _j === void 0 ? void 0 : _j.error) === null || _k === void 0 ? void 0 : _k.call(_j, '[Wishlist] Errore critico in addToWishlist:', error);
        onError(error);
    }
}
/**
 * Rimuove un prodotto dalla wishlist.
 *
 * @function removeFromWishlist
 * @param {SwymItem} product - Prodotto da rimuovere
 * @param {Function} onSuccess - Callback chiamata in caso di successo
 * @param {Function} onError - Callback chiamata in caso di errore
 * @returns {void}
 * @fires theme:wishlist:removed - Con il dettaglio del prodotto rimosso
 */
export function removeFromWishlist(product, onSuccess, onError) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    try {
        // Verifica che Swym SDK e wishlist siano disponibili
        if (typeof ((_a = window._swat) === null || _a === void 0 ? void 0 : _a.deleteFromList) !== 'function') {
            const error = new Error('Swym SDK non disponibile');
            (_c = (_b = window.LOG) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.call(_b, '[Wishlist] removeFromWishlist fallito:', error);
            onError(error);
            return;
        }
        if (!((_d = window.themeWishlist) === null || _d === void 0 ? void 0 : _d.lid)) {
            const error = new Error('Wishlist non caricata');
            (_f = (_e = window.LOG) === null || _e === void 0 ? void 0 : _e.error) === null || _f === void 0 ? void 0 : _f.call(_e, '[Wishlist] removeFromWishlist fallito:', error);
            onError(error);
            return;
        }
        (_h = (_g = window.LOG) === null || _g === void 0 ? void 0 : _g.debug) === null || _h === void 0 ? void 0 : _h.call(_g, '[Wishlist] → deleteFromList chiamato: lid=', window.themeWishlist.lid, 'product=', JSON.stringify(product));
        window._swat.deleteFromList(window.themeWishlist.lid, product, (data) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← deleteFromList risposta raw:', JSON.stringify(data));
            window.dispatchEvent(new CustomEvent('theme:wishlist:removed', { detail: data }));
            onSuccess(data);
        }, (err) => {
            var _a, _b;
            (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] ← deleteFromList errore:', err);
            onError(err);
        });
    }
    catch (error) {
        (_k = (_j = window.LOG) === null || _j === void 0 ? void 0 : _j.error) === null || _k === void 0 ? void 0 : _k.call(_j, '[Wishlist] Errore critico in removeFromWishlist:', error);
        onError(error);
    }
}
/**
 * Verifica se un prodotto è presente nella wishlist.
 *
 * @function isInWishlist
 * @param {SwymItem} product - Prodotto da verificare (serve solo `epi`)
 * @returns {boolean} `true` se il prodotto è in wishlist, `false` altrimenti
 */
export function isInWishlist(product) {
    if (!window.themeWishlist || !window.themeWishlist.listcontents)
        return false;
    return window.themeWishlist.listcontents.some(item => item.epi == product.epi);
}
/**
 * Alterna lo stato di un prodotto nella wishlist (add/remove toggle).
 *
 * Se il prodotto è già in wishlist, lo rimuove.
 * Se il prodotto non è in wishlist, lo aggiunge.
 *
 * @function toggleWishlistItem
 * @param {SwymItem} product - Prodotto da aggiungere/rimuovere
 * @param {Function} onSuccess - Callback chiamata in caso di successo
 * @param {Function} onError - Callback chiamata in caso di errore
 * @returns {void}
 */
export function toggleWishlistItem(product, onSuccess, onError) {
    var _a, _b;
    const action = isInWishlist(product) ? 'remove' : 'add';
    (_b = (_a = window.LOG) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.call(_a, '[Wishlist] toggleWishlistItem: epi=', product.epi, 'azione=', action);
    if (action === 'remove') {
        removeFromWishlist(product, onSuccess, onError);
    }
    else {
        addToWishlist(product, onSuccess, onError);
    }
}
// ============================================
// INIZIALIZZAZIONE AUTOMATICA
// ============================================
// Queste funzioni vengono eseguite automaticamente
// quando il modulo viene importato
loadCookie();
loadWishlist();
